/**
 * Suburb-landing content loader + content-uniqueness guard.
 *
 * The source of truth for every /locations/* page is /src/data/locations.json.
 * Each entry must be fully self-contained — no thin pages where only the
 * suburb name changes. The `validateUniqueness()` guard below is exercised
 * by lint/test runners to catch accidental duplication before publish.
 */
import locationsData from "@/data/locations.json";
import regionsData from "@/data/locationRegions.json";
import { SEO_DEFAULTS } from "@/lib/useSEO";

export const LOCATIONS = locationsData;
export const REGIONS = regionsData;

const BASE_URL = SEO_DEFAULTS.baseUrl;

// --- Lookup ---

export function getLocationBySlug(slug) {
  if (!slug) return null;
  const normalised = String(slug).toLowerCase();
  return LOCATIONS.find((loc) => loc.slug === normalised) || null;
}

export function getRegion(key) {
  return REGIONS.find((r) => r.key === key) || null;
}

export function locationUrl(slug) {
  return `${BASE_URL}/locations/${slug}`;
}

export function publishedLocations() {
  return LOCATIONS.filter((loc) => loc.publish_status === "published");
}

export function isDraft(loc) {
  return (loc?.publish_status || "draft") !== "published";
}

// --- Test-package mapping (refs catalog slugs surfaced via /api/products) ---

export const TEST_PACKAGE_META = {
  basic_water_safety_check: {
    label: "AWARE 4-Point",
    price_label: "$129",
    short_pitch: "E. coli, coliforms, pH, conductivity — the core safe-to-drink screen.",
    book_path: "/book-water-test?package=basic",
  },
  essential_tank_water_test: {
    label: "AWARE Tank 360",
    price_label: "$209",
    short_pitch: "Adds TDS, turbidity, alkalinity, hardness and metals — full rainwater-tank picture.",
    book_path: "/book-water-test?package=tank",
  },
  essential_bore_water_test: {
    label: "AWARE Bore 360",
    price_label: "$229",
    short_pitch: "Iron, manganese, hardness, conductivity, pH — tailored to bore chemistry.",
    book_path: "/book-water-test?package=bore",
  },
};

export function packageMeta(key) {
  return TEST_PACKAGE_META[key] || null;
}

// --- Thin-page protection (run by pytest + dev-time lint) ---

const _MIN_INTRO_WORDS = 80;
const _MIN_UNIQUE_RISKS = 2;
const _MIN_UNIQUE_FAQS = 1;

/** Return an array of human-readable validation problems. Empty = pass. */
export function validateUniqueness(loc, allLocs = LOCATIONS) {
  const problems = [];
  const intro = (loc.local_intro || "").trim();
  const introWords = intro.split(/\s+/).filter(Boolean).length;
  if (introWords < _MIN_INTRO_WORDS) {
    problems.push(`local_intro too short (${introWords} words; need >= ${_MIN_INTRO_WORDS})`);
  }
  const otherRisks = new Set();
  for (const other of allLocs) {
    if (other.slug === loc.slug) continue;
    for (const r of (other.common_water_risks || [])) otherRisks.add(r);
  }
  const uniqueRisks = (loc.common_water_risks || []).filter((r) => !otherRisks.has(r));
  if (uniqueRisks.length < _MIN_UNIQUE_RISKS) {
    problems.push(`needs >= ${_MIN_UNIQUE_RISKS} suburb-unique risks (has ${uniqueRisks.length})`);
  }
  const otherFAQs = new Set();
  for (const other of allLocs) {
    if (other.slug === loc.slug) continue;
    for (const f of (other.faq_items || [])) otherFAQs.add(f.q);
  }
  const uniqueFAQs = (loc.faq_items || []).filter((f) => !otherFAQs.has(f.q));
  if (uniqueFAQs.length < _MIN_UNIQUE_FAQS) {
    problems.push(`needs >= ${_MIN_UNIQUE_FAQS} suburb-unique FAQ (has ${uniqueFAQs.length})`);
  }
  if (!loc.local_intro || !loc.seo_title || !loc.seo_description) {
    problems.push("missing required SEO fields (local_intro/seo_title/seo_description)");
  }
  return problems;
}

/** Throws if any location fails. Useful in tests. */
export function assertAllLocationsUnique() {
  const issues = [];
  for (const loc of LOCATIONS) {
    const problems = validateUniqueness(loc);
    if (problems.length) issues.push({ slug: loc.slug, problems });
  }
  if (issues.length) {
    const msg = issues.map((i) => `  ${i.slug}: ${i.problems.join(", ")}`).join("\n");
    throw new Error(`Location content thin-page validation failed:\n${msg}`);
  }
}
